import usePrivateAxios from '@/common/api/usePrivateAxios'
import { mapToDeletion } from '@/common/utils/railsHelpers'
import { unFormatCurrency } from '@/common/utils/unFormatters'
import { UpdateContractCreditLimitsSchema } from '@/features/privateCompanies/schemas/contract/updateCreditLimits.schema'
import { CompanyContractType } from '@/features/privateCompanies/types/companyContract.type'
import { transformNewContractProductCategory } from '@/features/publicCompanyLeadsFunnel/services/publicCompanyLeadProductCategoryTransformers'
import { useMutation } from '@tanstack/react-query'

interface UseUpdateContractCreditLimits {
  contractId?: string
  companyKind?: string
  isConsumptionControlledByQuantity?: boolean
  contractProductCategories: CompanyContractType['contract_product_categories']
  onSuccess?: (response: any) => void
  onError?: (error: any) => void
}

export const useUpdateContractCreditLimits = ({
  contractId,
  companyKind,
  isConsumptionControlledByQuantity,
  contractProductCategories,
  onSuccess,
  onError,
}: UseUpdateContractCreditLimits) => {
  const privateAxios = usePrivateAxios()

  return useMutation({
    mutationKey: ['updateContractCreditLimits'],
    mutationFn: async (data: UpdateContractCreditLimitsSchema) => {
      const contract: {
        global_value: number | undefined
        granted_credit: number | undefined
        default_fuel_price_rule: string | undefined
        require_product_credit_quota: boolean
        contract_product_categories_attributes: any[]
      } = {
        global_value: undefined,
        granted_credit: undefined,
        default_fuel_price_rule: data.contract?.default_fuel_price_rule?.value || undefined,
        require_product_credit_quota: data.contract?.require_product_credit_quota,
        contract_product_categories_attributes: [
          ...data?.contract?.contract_product_categories_attributes?.map((contractProductCategory) => ({
            id: contractProductCategory.id,
            ...transformNewContractProductCategory(
              {
                ...contractProductCategory,
                require_product_credit_quota: data.contract?.require_product_credit_quota,
              },
              contractProductCategories,
              isConsumptionControlledByQuantity,
            ),
          })),
          ...contractProductCategories
            .filter((item) => {
              const dataIds =
                data?.contract?.contract_product_categories_attributes
                  ?.map((dataItem) => dataItem.id)
                  .filter(Boolean) || []
              return !dataIds.includes(item.id)
            })
            .map(mapToDeletion),
        ],
      }

      if (companyKind === 'public_company') {
        contract.global_value = data.contract?.granted_credit
          ? unFormatCurrency(data.contract.granted_credit)
          : undefined
      } else {
        contract.granted_credit = data.contract?.granted_credit
          ? unFormatCurrency(data.contract.granted_credit)
          : undefined
      }

      await privateAxios.put(`/client/contracts/${contractId}/update_contract_credits_information`, {
        contract,
      })
    },
    onSuccess,
    onError,
  })
}
